/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Utils, TLBounds } from "@tldraw/core";
import { Vec } from "@tldraw/vec";
import type { TldrawApp } from "pages/flyer/builder/drawer/state/internal";
import { TLDR } from "pages/flyer/builder/drawer/state/TLDR";
import {
  AlignType,
  PAGE_ID,
  TDShape,
  TDShapeType,
  TemplateIdsEnum,
  TldrawCommand,
  IDeltaMap,
} from "pages/flyer/builder/drawer/types";

export function alignShapes(app: TldrawApp, ids: string[], type: AlignType): TldrawCommand {
  const { currentPageId } = app;

  const initialShapes = ids.map((id) => app.getShape(id));

  const parentId = initialShapes.some((shape) => shape.parentId !== initialShapes[0].parentId)
    ? PAGE_ID
    : initialShapes[0].parentId;

  const boundsForShapes = initialShapes.map((shape) => {
    return {
      id: shape.id,
      point: [...shape.point],
      bounds: TLDR.getBounds(shape),
    };
  });

  const commonBounds = Utils.getCommonBounds(boundsForShapes.map(({ bounds }) => bounds));
  const centerPointOfSelectedShapes = [
    commonBounds.minX + commonBounds.width / 2,
    commonBounds.minY + commonBounds.height / 2,
  ];

  let parentBounds: TLBounds;

  if (parentId === PAGE_ID) {
    const frontTemplateShape = app.getShape(TemplateIdsEnum.Front);
    const frontBounds = TLDR.getBounds(frontTemplateShape);
    const centerPointOfFront = [frontBounds.minX + frontBounds.width / 2, frontBounds.minY + frontBounds.height / 2];
    const distanceForFront = Math.hypot(
      centerPointOfSelectedShapes[0] - centerPointOfFront[0],
      centerPointOfSelectedShapes[1] - centerPointOfFront[1]
    );

    const backTemplateShape = app.getShape(TemplateIdsEnum.Back);
    const backBounds = TLDR.getBounds(backTemplateShape);
    const centerPointOfBack = [backBounds.minX + backBounds.width / 2, backBounds.minY + backBounds.height / 2];
    const distanceForBack = Math.hypot(
      centerPointOfSelectedShapes[0] - centerPointOfBack[0],
      centerPointOfSelectedShapes[1] - centerPointOfBack[1]
    );

    const nearestTemplateBounds = distanceForFront <= distanceForBack ? frontBounds : backBounds;
    parentBounds = { ...nearestTemplateBounds };
  } else {
    parentBounds = TLDR.getBounds(app.getShape(parentId));
  }

  // Determine alignment target based on number of selected shapes
  let alignmentTarget: TLBounds;

  if (initialShapes.length === 1) {
    // Single element: align with canvas
    alignmentTarget = parentBounds;
  } else {
    // Multiple elements: align with the furthest element in the direction of alignment
    const sortedShapes = boundsForShapes.sort((a, b) => {
      switch (type) {
        case AlignType.Left:
          return a.bounds.minX - b.bounds.minX;
        case AlignType.Right:
          return b.bounds.maxX - a.bounds.maxX;
        case AlignType.Top:
          return a.bounds.minY - b.bounds.minY;
        case AlignType.Bottom:
          return b.bounds.maxY - a.bounds.maxY;
        default:
          return 0;
      }
    });

    // Use the first shape (furthest in the alignment direction) as target
    alignmentTarget = sortedShapes[0].bounds;
  }

  const midX = alignmentTarget.minX + alignmentTarget.width / 2;
  const midY = alignmentTarget.minY + alignmentTarget.height / 2;

  const delta = {
    [AlignType.CenterVertical]: [0, midY - commonBounds.height / 2 - commonBounds.minY],
    [AlignType.CenterHorizontal]: [midX - commonBounds.width / 2 - commonBounds.minX, 0],
    [AlignType.Top]: [0, alignmentTarget.minY - commonBounds.minY],
    [AlignType.Bottom]: [0, alignmentTarget.maxY - commonBounds.height - commonBounds.minY],
    [AlignType.Left]: [alignmentTarget.minX - commonBounds.minX, 0],
    [AlignType.Right]: [alignmentTarget.maxX - commonBounds.width - commonBounds.minX, 0],
  }[type];

  const updatedIds: string[] = [];
  const deltaMap: IDeltaMap = {};

  const dfs = (shape: TDShape) => {
    if (shape.type === TDShapeType.Group) {
      shape.children.forEach((id) => {
        const child = app.getShape(id);
        dfs(child);
      });
    } else {
      updatedIds.push(shape.id);
      deltaMap[shape.id] = {
        prev: shape.point,
        next: Vec.add(shape.point, delta),
      };
    }
  };

  initialShapes.forEach((shape) => {
    dfs(shape);
  });

  const { before, after } = TLDR.mutateShapes(
    app.state,
    updatedIds,
    (shape) => {
      if (!deltaMap[shape.id]) return shape;
      return { point: deltaMap[shape.id].next };
    },
    currentPageId
  );

  return {
    id: "align",
    before: {
      document: {
        pages: {
          [currentPageId]: {
            shapes: before,
          },
        },
        pageStates: {
          [currentPageId]: {
            selectedIds: ids,
          },
        },
      },
    },
    after: {
      document: {
        pages: {
          [currentPageId]: {
            shapes: after,
          },
        },
        pageStates: {
          [currentPageId]: {
            selectedIds: ids,
          },
        },
      },
    },
  };
}
